/**
 * Marketplace Pricing Table Component
 * Fixed 16-column pricing table with full cost breakdown
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Download,
  Search,
  X,
  Settings,
  ChevronLeft,
  Globe,
  RefreshCw,
  DollarSign,
  Package,
  Truck,
  Filter,
  Upload,
  Calculator,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type {
  CustomMarketplace,
  CustomMarketplaceProductData,
  FulfillmentOption,
  CategoryColumnDefaults,
} from '../../types/database';
import type { Product } from '../../types';
import type { ShippingRateBracket } from '../../types/pricing';
import { MARKETPLACE_LOGOS, type SettingsTab } from './types';
import { getAllProducts } from '../../utils/database';
import { getRecommendedPrice, calculateShippingCost } from '../../utils/pricing';
import { apiGetProfitConfig, apiGetShippingRates, type MarketplaceProfitConfig } from '../../services/api';
import MarketplaceSKUImportModal from './MarketplaceSKUImportModal';
import { logger } from '../../utils/logger';

interface MarketplacePricingTableProps {
  selectedMarketplace: CustomMarketplace;
  selectedCountry: string;
  selectedFulfillment: string;
  products: Product[];
  productData: CustomMarketplaceProductData[];
  exchangeRates: Record<string, number>;
  categoryDefaults: CategoryColumnDefaults[];
  fulfillmentOptions: FulfillmentOption[];
  isLoadingRates: boolean;

  // Setters
  setSelectedCountry: (code: string) => void;
  setSelectedFulfillment: (code: string) => void;
  setViewMode: (mode: 'grid' | 'table' | 'settings') => void;
  setSettingsTab: (tab: SettingsTab) => void;
  setProducts: (products: Product[]) => void;

  // Handlers
  onBackToGrid: () => void;
  onRefreshRates: () => Promise<void>;
}

interface ProductRow {
  product: Product;
  fbaSkus: CustomMarketplaceProductData[];
  fbmSkus: CustomMarketplaceProductData[];
  allSkus: CustomMarketplaceProductData[];
}

interface ProductBreakdown {
  advisedPrice: number;
  sellingPrice: number;
  netProfit: number;
  marginPercent: number;
  // Percent-based costs (% and amount)
  sellingFeePercent: number;
  sellingFeeAmount: number;
  fbaFeePercent: number;
  fbaFeeAmount: number;
  refundLossPercent: number;
  refundLossAmount: number;
  vatPercent: number;
  vatAmount: number;
  adsPercent: number;
  adsAmount: number;
  fbaCostPercent: number;
  fbaCostAmount: number;
  fbmCostPercent: number;
  fbmCostAmount: number;
  warehousePercent: number;
  warehouseAmount: number;
  gstPercent: number;
  gstAmount: number;
  // Fixed costs
  productCost: number;
  shippingCost: number;
  customsPercent: number;
  customsAmount: number;
  ddpFee: number;
  // Totals
  totalExpenses: number;
}

// products.desi_eff = the house desi rule (dims/5000 ‖ manual_size ‖ size) — the same value ManuMaestro and
// AmzSellMetrics read (audit 2026-09-24, X3). Hand-built products (simulator) carry no desiEff → size.
const productDesi = (p: Product): number => Number(p.desiEff !== undefined ? p.desiEff : p.size) || 0;
const desiFromManual = (p: Product): boolean =>
  !!Number(p.manualSize) && !(Number(p.length) > 0 && Number(p.width) > 0 && Number(p.height) > 0);

const EMPTY_BREAKDOWN: ProductBreakdown = {
  advisedPrice: 0, sellingPrice: 0, netProfit: 0, marginPercent: 0,
  sellingFeePercent: 0, sellingFeeAmount: 0, fbaFeePercent: 0, fbaFeeAmount: 0,
  refundLossPercent: 0, refundLossAmount: 0, vatPercent: 0, vatAmount: 0,
  adsPercent: 0, adsAmount: 0, fbaCostPercent: 0, fbaCostAmount: 0,
  fbmCostPercent: 0, fbmCostAmount: 0, warehousePercent: 0, warehouseAmount: 0,
  gstPercent: 0, gstAmount: 0, productCost: 0, shippingCost: 0,
  customsPercent: 0, customsAmount: 0, ddpFee: 0, totalExpenses: 0,
};

const MarketplacePricingTable: React.FC<MarketplacePricingTableProps> = ({
  selectedMarketplace,
  selectedCountry,
  selectedFulfillment,
  products,
  productData,
  exchangeRates,
  categoryDefaults,
  fulfillmentOptions,
  isLoadingRates,
  setSelectedCountry,
  setSelectedFulfillment,
  setViewMode,
  setSettingsTab,
  setProducts,
  onBackToGrid,
  onRefreshRates,
}) => {
  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterParent, setFilterParent] = useState('');
  const [hideIncomplete, setHideIncomplete] = useState(true);
  const [isSKUImportOpen, setIsSKUImportOpen] = useState(false);

  // Simulator state
  const [showSimulator, setShowSimulator] = useState(false);
  const [simCost, setSimCost] = useState<number>(0);
  const [simDesi, setSimDesi] = useState<number>(0);
  const [simCategory, setSimCategory] = useState('');

  // Full profit config (FBA + FBM + GST)
  const [profitConfig, setProfitConfig] = useState<MarketplaceProfitConfig | null>(null);
  const [shippingBrackets, setShippingBrackets] = useState<ShippingRateBracket[]>([]);
  const [usUsShippingBrackets, setUsUsShippingBrackets] = useState<ShippingRateBracket[]>([]);

  // Load profit configs and shipping rates
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const config = await apiGetProfitConfig(selectedCountry);
        setProfitConfig(config || null);

        // Load shipping rates for FBM (route: {country}-TR)
        const routeCode = `${selectedCountry}-TR`;
        try {
          const shippingData = await apiGetShippingRates(routeCode);
          setShippingBrackets(shippingData?.brackets || []);
        } catch {
          setShippingBrackets([]);
        }

        // For US, also load US-US route (FBM-US)
        if (selectedCountry === 'US') {
          try {
            const usUsData = await apiGetShippingRates('US-US');
            setUsUsShippingBrackets(usUsData?.brackets || []);
          } catch {
            setUsUsShippingBrackets([]);
          }
        } else {
          setUsUsShippingBrackets([]);
        }
      } catch (error) {
        logger.error('Failed to load profit configs:', error);
      }
    };

    loadConfigs();
  }, [selectedCountry]);

  // Computed values
  const selectedCountryData = selectedMarketplace.countries.find(c => c.code === selectedCountry);
  const fbaConfig = profitConfig?.fba_config;
  const fbmConfig = profitConfig?.fbm_config;
  const gstConfig = profitConfig?.gst_config;

  // Determine which optional columns to show
  const fulfillmentType = selectedFulfillment || 'FBA';
  const isFBA = fulfillmentType === 'FBA';
  const isFBMTR = fulfillmentType === 'FBM-TR';
  const isFBM = !isFBA;

  const showFbaFee = isFBA;
  const showFbaCost = isFBA;
  const showFbmCost = isFBM;
  const showCustoms = isFBMTR;
  const showDdp = isFBMTR;
  const showWarehouse = !!(fbaConfig && fbaConfig.warehousePercent > 0);
  const showGst = !!(gstConfig && gstConfig.gstPercent > 0);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    products.forEach(p => { if (p.category) cats.add(p.category); });
    return Array.from(cats).sort();
  }, [products]);

  const parentProducts = useMemo(() => {
    const parents = new Set<string>();
    products.forEach(p => { if (p.name) parents.add(p.name); });
    return Array.from(parents).sort();
  }, [products]);

  // Product-centric rows
  const productRows = useMemo((): ProductRow[] => {
    const rowsMap = new Map<string, ProductRow>();
    products.forEach(product => {
      const productSkus = productData.filter(data => data.productId === product.id);
      if (productSkus.length > 0) {
        rowsMap.set(product.id, {
          product,
          fbaSkus: productSkus.filter(s => s.fulfillmentType === 'FBA'),
          fbmSkus: productSkus.filter(s => s.fulfillmentType?.startsWith('FBM')),
          allSkus: productSkus
        });
      }
    });
    return Array.from(rowsMap.values());
  }, [products, productData]);

  // Filtered rows
  const filteredProductRows = useMemo(() => {
    let filtered = productRows;

    if (hideIncomplete) {
      filtered = filtered.filter(row => {
        const p = row.product;
        if (!p.baseCost || p.baseCost <= 0) return false;
        if (!productDesi(p)) return false;
        return true;
      });
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(row =>
        row.product.name.toLowerCase().includes(term) ||
        (row.product.category || '').toLowerCase().includes(term) ||
        (row.product.productSku || '').toLowerCase().includes(term)
      );
    }

    if (filterCategory) {
      filtered = filtered.filter(row => row.product.category === filterCategory);
    }

    if (filterParent) {
      filtered = filtered.filter(row => row.product.name === filterParent);
    }

    if (selectedFulfillment) {
      filtered = filtered.filter(row =>
        row.allSkus.some(sku => {
          if (sku.countryCode !== selectedCountry) return false;
          if (sku.fulfillmentType === selectedFulfillment) return true;
          if ((selectedFulfillment === 'FBM-US' || selectedFulfillment === 'FBM-TR') &&
              sku.fulfillmentType === 'FBM') return true;
          return false;
        })
      );
    } else {
      filtered = filtered.filter(row =>
        row.allSkus.some(sku => sku.countryCode === selectedCountry)
      );
    }

    filtered.sort((a, b) => a.product.name.localeCompare(b.product.name, 'tr'));

    return filtered;
  }, [productRows, hideIncomplete, searchTerm, filterCategory, filterParent, selectedFulfillment, selectedCountry]);

  // Get expense rates from category defaults
  const getExpenseRates = useCallback((product: Product, ft: string): Record<string, number> => {
    const rates: Record<string, number> = {};

    let defaults = categoryDefaults.find(d =>
      d.marketplaceId === selectedMarketplace.id &&
      d.countryCode === selectedCountry &&
      d.category === product.category &&
      d.fulfillmentType === ft
    );

    // Fallback: FBM → FBM-TR (US)
    if (!defaults && ft === 'FBM' && selectedCountry === 'US') {
      defaults = categoryDefaults.find(d =>
        d.marketplaceId === selectedMarketplace.id &&
        d.countryCode === selectedCountry &&
        d.category === product.category &&
        d.fulfillmentType === 'FBM-TR'
      );
    }

    // Fallback: FBM-TR/FBM-US → FBM
    if (!defaults && (ft === 'FBM-TR' || ft === 'FBM-US')) {
      defaults = categoryDefaults.find(d =>
        d.marketplaceId === selectedMarketplace.id &&
        d.countryCode === selectedCountry &&
        d.category === product.category &&
        d.fulfillmentType === 'FBM'
      );
    }

    // Fallback: __default__ category (same country + fulfillment)
    if (!defaults) {
      defaults = categoryDefaults.find(d =>
        d.marketplaceId === selectedMarketplace.id &&
        d.countryCode === selectedCountry &&
        d.category === '__default__' &&
        d.fulfillmentType === ft
      );
    }

    // Fallback: __default__ + FBM variants
    if (!defaults && (ft === 'FBM-TR' || ft === 'FBM-US')) {
      defaults = categoryDefaults.find(d =>
        d.marketplaceId === selectedMarketplace.id &&
        d.countryCode === selectedCountry &&
        d.category === '__default__' &&
        d.fulfillmentType === 'FBM'
      );
    }

    if (defaults) {
      Object.entries(defaults.columnDefaults).forEach(([key, value]) => {
        if (typeof value === 'number') rates[key] = value;
      });
    }

    return rates;
  }, [selectedMarketplace, selectedCountry, categoryDefaults]);

  // Get target margin
  const getTargetMargin = useCallback((product: Product, ft: string): number => {
    let defaults = categoryDefaults.find(d =>
      d.marketplaceId === selectedMarketplace.id &&
      d.countryCode === selectedCountry &&
      d.category === product.category &&
      d.fulfillmentType === ft
    );

    if (!defaults && ft === 'FBM' && selectedCountry === 'US') {
      defaults = categoryDefaults.find(d =>
        d.marketplaceId === selectedMarketplace.id &&
        d.countryCode === selectedCountry &&
        d.category === product.category &&
        d.fulfillmentType === 'FBM-TR'
      );
    }

    if (!defaults && (ft === 'FBM-TR' || ft === 'FBM-US')) {
      defaults = categoryDefaults.find(d =>
        d.marketplaceId === selectedMarketplace.id &&
        d.countryCode === selectedCountry &&
        d.category === product.category &&
        d.fulfillmentType === 'FBM'
      );
    }

    // Fallback: __default__ category
    if (!defaults) {
      defaults = categoryDefaults.find(d =>
        d.marketplaceId === selectedMarketplace.id &&
        d.countryCode === selectedCountry &&
        d.category === '__default__' &&
        d.fulfillmentType === ft
      );
    }

    if (!defaults && (ft === 'FBM-TR' || ft === 'FBM-US')) {
      defaults = categoryDefaults.find(d =>
        d.marketplaceId === selectedMarketplace.id &&
        d.countryCode === selectedCountry &&
        d.category === '__default__' &&
        d.fulfillmentType === 'FBM'
      );
    }

    return defaults?.targetMargin || 20;
  }, [selectedMarketplace, selectedCountry, categoryDefaults]);

  // Parse JSON configs safely
  const parseFbaConfig = useCallback(() => {
    if (!fbaConfig) return null;
    const cfg = typeof fbaConfig === 'string' ? JSON.parse(fbaConfig) : fbaConfig;
    return {
      shippingPerDesi: Number(cfg.shippingPerDesi) || 0,
      warehousePercent: Number(cfg.warehousePercent) || 0,
    };
  }, [fbaConfig]);

  const parseFbmConfig = useCallback(() => {
    if (!fbmConfig) return null;
    const cfg = typeof fbmConfig === 'string' ? JSON.parse(fbmConfig) : fbmConfig;
    return {
      shippingMode: cfg.shippingMode || 'TR',
      fromTR: {
        customsDutyPercent: Number(cfg.fromTR?.customsDutyPercent) || 0,
        ddpFee: Number(cfg.fromTR?.ddpFee) || 0,
        categoryDuties: cfg.fromTR?.categoryDuties || [],
      },
      fromLocal: cfg.fromLocal ? {
        warehousePercent: Number(cfg.fromLocal.warehousePercent) || 0,
      } : undefined,
    };
  }, [fbmConfig]);

  const parseGstConfig = useCallback(() => {
    if (!gstConfig) return null;
    const cfg = typeof gstConfig === 'string' ? JSON.parse(gstConfig) : gstConfig;
    if (!cfg.enabled) return null;
    return {
      gstPercent: Number(cfg.ratePercent || cfg.gstPercent || cfg.rate) || 0,
      includedInPrice: !!cfg.includedInPrice,
      applyTo: cfg.applyTo || 'BOTH',
    };
  }, [gstConfig]);

  // Full product breakdown calculation
  const calculateBreakdown = useCallback((product: Product, ft: string): ProductBreakdown => {
    if (!product.baseCost || !selectedCountry) return EMPTY_BREAKDOWN;

    const country = selectedMarketplace.countries.find(c => c.code === selectedCountry);
    const currency = country?.currency || 'USD';
    const rate = exchangeRates[currency] || 1;
    const productCost = product.baseCost * rate;
    const desi = productDesi(product);

    const expenseRates = getExpenseRates(product, ft);
    const targetMargin = getTargetMargin(product, ft);

    const fba = parseFbaConfig();
    const fbm = parseFbmConfig();
    const gst = parseGstConfig();

    const currentIsFBA = ft === 'FBA';
    const currentIsFBMTR = ft === 'FBM-TR';
    const currentIsFBMUS = ft === 'FBM-US';

    // === FIXED COSTS ===
    let shippingCost = 0;
    if (currentIsFBA && fba) {
      shippingCost = desi * fba.shippingPerDesi * rate;
    } else if (currentIsFBMTR && shippingBrackets.length > 0) {
      shippingCost = calculateShippingCost(shippingBrackets, desi);
    } else if (currentIsFBMUS && fba) {
      const trToUs = desi * fba.shippingPerDesi * rate;
      const usToCustomer = usUsShippingBrackets.length > 0
        ? calculateShippingCost(usUsShippingBrackets, desi) : 0;
      shippingCost = trToUs + usToCustomer;
    } else if (!currentIsFBA && shippingBrackets.length > 0) {
      shippingCost = calculateShippingCost(shippingBrackets, desi);
    }

    // Customs duty (FBM-TR only, % of product cost)
    let customsPercent = 0;
    let customsAmount = 0;
    if (currentIsFBMTR && fbm) {
      // Check category-specific duty
      const catDuty = fbm.fromTR.categoryDuties?.find(
        (d: { category: string; dutyPercent: number }) => d.category === product.category
      );
      customsPercent = catDuty ? catDuty.dutyPercent : fbm.fromTR.customsDutyPercent;
      customsAmount = (customsPercent / 100) * productCost;
    }

    // DDP fee (FBM-TR only, fixed USD → local)
    let ddpFee = 0;
    if (currentIsFBMTR && fbm) {
      ddpFee = fbm.fromTR.ddpFee * rate;
    }

    const totalFixedCosts = productCost + shippingCost + customsAmount + ddpFee;

    // === PERCENT-BASED COSTS (% of selling price) ===
    // DB keys: sellingPercent, fbaFeePercent, refundPercent, vatPercent, adsPercent, fbaCostPercent, fbmCostPercent
    const sellingFeePercent = expenseRates['sellingPercent'] || expenseRates['sellingFeePercent'] || expenseRates['platformFee'] || 0;
    const fbaFeePercent = currentIsFBA ? (expenseRates['fbaFeePercent'] || expenseRates['fulfillmentFee'] || 0) : 0;
    const refundLossPercent = expenseRates['refundPercent'] || expenseRates['refundLossPercent'] || 0;
    const vatPercent = expenseRates['vatPercent'] || 0;
    const adsPercent = expenseRates['adsPercent'] || expenseRates['advertisingPercent'] || 0;
    const fbaCostPercent = currentIsFBA ? (expenseRates['fbaCostPercent'] || 0) : 0;
    const fbmCostPercent = !currentIsFBA ? (expenseRates['fbmCostPercent'] || 0) : 0;

    // Warehouse (from profit config)
    let warehousePercent = 0;
    if (currentIsFBA && fba) {
      warehousePercent = fba.warehousePercent;
    } else if (currentIsFBMUS && fbm?.fromLocal) {
      warehousePercent = fbm.fromLocal.warehousePercent;
    }

    // GST (from profit config)
    let gstPercent = 0;
    if (gst) {
      const applyTo = gst.applyTo;
      if (applyTo === 'BOTH' || (applyTo === 'FBA' && currentIsFBA) || (applyTo === 'FBM' && !currentIsFBA)) {
        gstPercent = gst.gstPercent;
      }
    }

    const totalPercentDecimal = (sellingFeePercent + fbaFeePercent + refundLossPercent + vatPercent +
      adsPercent + fbaCostPercent + fbmCostPercent + warehousePercent + gstPercent) / 100;
    const targetMarginDecimal = targetMargin / 100;

    // === REVERSE FORMULA ===
    const denominator = 1 - totalPercentDecimal - targetMarginDecimal;
    if (denominator <= 0) {
      return { ...EMPTY_BREAKDOWN, productCost, shippingCost, customsPercent, customsAmount, ddpFee };
    }

    const sellingPrice = totalFixedCosts / denominator;
    const advisedPrice = getRecommendedPrice(sellingPrice);

    // === FORWARD: compute amounts ===
    const sellingFeeAmount = sellingPrice * sellingFeePercent / 100;
    const fbaFeeAmount = sellingPrice * fbaFeePercent / 100;
    const refundLossAmount = sellingPrice * refundLossPercent / 100;
    const vatAmount = sellingPrice * vatPercent / 100;
    const adsAmount = sellingPrice * adsPercent / 100;
    const fbaCostAmount = sellingPrice * fbaCostPercent / 100;
    const fbmCostAmount = sellingPrice * fbmCostPercent / 100;
    const warehouseAmount = sellingPrice * warehousePercent / 100;
    const gstAmount = sellingPrice * gstPercent / 100;

    const totalPercentAmounts = sellingFeeAmount + fbaFeeAmount + refundLossAmount + vatAmount +
      adsAmount + fbaCostAmount + fbmCostAmount + warehouseAmount + gstAmount;
    const totalExpenses = shippingCost + customsAmount + ddpFee + totalPercentAmounts;

    const netProfit = sellingPrice - productCost - totalExpenses;
    const marginPercent = sellingPrice > 0 ? (netProfit / sellingPrice) * 100 : 0;

    return {
      advisedPrice, sellingPrice, netProfit, marginPercent,
      sellingFeePercent, sellingFeeAmount, fbaFeePercent, fbaFeeAmount,
      refundLossPercent, refundLossAmount, vatPercent, vatAmount,
      adsPercent, adsAmount, fbaCostPercent, fbaCostAmount,
      fbmCostPercent, fbmCostAmount, warehousePercent, warehouseAmount,
      gstPercent, gstAmount, productCost, shippingCost,
      customsPercent, customsAmount, ddpFee, totalExpenses,
    };
  }, [selectedMarketplace, selectedCountry, exchangeRates, getExpenseRates, getTargetMargin,
      parseFbaConfig, parseFbmConfig, parseGstConfig, shippingBrackets, usUsShippingBrackets]);

  // Export data
  const exportData = () => {
    if (filteredProductRows.length === 0) return;

    const country = selectedMarketplace.countries.find(c => c.code === selectedCountry);
    const sym = country?.currencySymbol || '$';
    const ft = selectedFulfillment || 'FBA';

    const headers = [
      'Product', 'Category', 'Internal SKU',
      `Marketplace SKUs${selectedFulfillment ? ` (${selectedFulfillment})` : ''}`,
      'Base Cost (USD)', 'Size (Desi)',
      `Advised Price (${sym})`, `Min Price (${sym})`,
      `Net Profit (${sym})`, 'Margin %',
      'Selling Fee %', ...(showFbaFee ? ['FBA Fee %'] : []),
      'Refund %', 'VAT %', 'Ads %',
      ...(showFbaCost ? ['FBA Cost %'] : []),
      ...(showFbmCost ? ['FBM Cost %'] : []),
      `Shipping (${sym})`,
      ...(showCustoms ? ['Customs %'] : []),
      ...(showDdp ? [`DDP (${sym})`] : []),
      ...(showWarehouse ? ['Warehouse %'] : []),
      ...(showGst ? ['GST %'] : []),
    ];

    const rows = filteredProductRows.map(row => {
      const { product } = row;
      const currentCountrySkus = row.allSkus.filter(s => {
        if (s.countryCode !== selectedCountry) return false;
        if (selectedFulfillment) {
          if (s.fulfillmentType === selectedFulfillment) return true;
          if ((selectedFulfillment === 'FBM-US' || selectedFulfillment === 'FBM-TR') &&
              s.fulfillmentType === 'FBM') return true;
          return false;
        }
        return true;
      });
      const b = calculateBreakdown(product, ft);

      const values: string[] = [
        product.name, product.category || '', product.productSku || '',
        currentCountrySkus.map(s => s.sku).join(';'),
        (product.baseCost || 0).toFixed(2), productDesi(product).toFixed(2),
        b.advisedPrice.toFixed(2), b.sellingPrice.toFixed(2),
        b.netProfit.toFixed(2), b.marginPercent.toFixed(1),
        b.sellingFeePercent.toFixed(1), ...(showFbaFee ? [b.fbaFeePercent.toFixed(1)] : []),
        b.refundLossPercent.toFixed(1), b.vatPercent.toFixed(1), b.adsPercent.toFixed(1),
        ...(showFbaCost ? [b.fbaCostPercent.toFixed(1)] : []),
        ...(showFbmCost ? [b.fbmCostPercent.toFixed(1)] : []),
        b.shippingCost.toFixed(2),
        ...(showCustoms ? [b.customsPercent.toFixed(1)] : []),
        ...(showDdp ? [b.ddpFee.toFixed(2)] : []),
        ...(showWarehouse ? [b.warehousePercent.toFixed(1)] : []),
        ...(showGst ? [b.gstPercent.toFixed(1)] : []),
      ];

      return values.join('\t');
    });

    const tsv = [headers.join('\t'), ...rows].join('\n');
    const blob = new Blob([tsv], { type: 'text/tab-separated-values' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pricelab-${selectedMarketplace.shortName}-${selectedCountry}-${selectedFulfillment || 'all'}-${new Date().toISOString().split('T')[0]}.tsv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Show empty state if no countries
  if (selectedMarketplace.countries.length === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-3">
            <button onClick={onBackToGrid} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="flex items-center gap-3">
              <span className="text-2xl">{selectedMarketplace.icon}</span>
              <h2 className="font-semibold text-slate-800">{selectedMarketplace.name}</h2>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Globe className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Önce Ülke Ekleyin</h2>
          <p className="text-slate-500 mb-6">Bu pazaryeri için en az bir ülke/market tanımlamalısınız.</p>
          <button
            onClick={() => { setViewMode('settings'); setSettingsTab('countries'); }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
          >
            <Settings className="w-4 h-4" />
            Ayarlara Git
          </button>
        </div>
      </div>
    );
  }

  // Helper: render SKU cell
  const renderSkuCell = (skus: CustomMarketplaceProductData[], fulfillmentFilter?: string) => {
    const filtered = skus.filter(s => {
      if (s.countryCode !== selectedCountry) return false;
      if (fulfillmentFilter) {
        if (s.fulfillmentType === fulfillmentFilter) return true;
        if ((fulfillmentFilter === 'FBM-US' || fulfillmentFilter === 'FBM-TR') &&
            s.fulfillmentType === 'FBM') return true;
        return false;
      }
      return true;
    });

    if (filtered.length === 0) return <span className="text-slate-400">-</span>;
    if (filtered.length === 1) return <span className="text-[11px] text-slate-700 break-all">{filtered[0].sku}</span>;
    if (filtered.length <= 3) {
      return (
        <div className="flex flex-col gap-0.5">
          {filtered.map((s, i) => <span key={i} className="text-[11px] text-slate-700 break-all">{s.sku}</span>)}
        </div>
      );
    }
    const allSkus = filtered.map(s => s.sku).join(', ');
    return (
      <span className="text-[11px] text-slate-700 cursor-help break-all" title={allSkus}>
        {filtered[0].sku} <span className="text-slate-500">+{filtered.length - 1}</span>
      </span>
    );
  };

  // Helper: percent cell
  const pctCell = (pct: number, amount: number, sym: string) => (
    <td className="px-2 py-2 text-right" style={{ minWidth: '55px' }}>
      <div className="text-[11px] text-slate-600">{pct.toFixed(1)}%</div>
      {amount > 0 && <div className="text-[9px] text-slate-400">{sym}{amount.toFixed(2)}</div>}
    </td>
  );

  // Helper: fixed cost cell
  const fixedCell = (amount: number, sym: string, subtitle?: string) => (
    <td className="px-2 py-2 text-right" style={{ minWidth: '60px' }}>
      <div className="text-[11px] text-slate-600">{sym}{amount.toFixed(2)}</div>
      {subtitle && <div className="text-[9px] text-slate-400">{subtitle}</div>}
    </td>
  );

  // Header cell helper
  const th = (label: string, extra?: string) => (
    <th className={`px-2 py-2 text-right text-[11px] font-semibold whitespace-nowrap ${extra || 'text-slate-600'}`} style={{ minWidth: '55px' }}>
      {label}
    </th>
  );

  const currencySymbol = selectedCountryData?.currencySymbol || '$';

  return (
    <div className="space-y-3">
      {/* Denetim 09-24 P1 (karar: dondur, kur düzeltmesi yok): kur tarayıcıdan api.frankfurter.app'ten geliyor; adres
          CORS başlıksız 301 dönüyor → sabit yedek kur (USD/TRY 34,50; canlı ~48,9). AED/SAR/EGP kaynakta yok → 1 sayılıyor. */}
      <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-lg p-4 text-sm">
        <b>Bu tablodaki kurlar güvenilir değil — fiyat kararı için kullanmayın.</b> Kur servisi yanıt vermediğinde
        tablo eski bir sabit kurla hesaplıyor (USD/TRY 34,50; güncel kur ~48,9) ve AED, SAR, EGP kurlarını hiç
        bulamıyor. Tablo bu haliyle referans olarak duruyor, <b>güncellenmiyor</b>.
      </div>

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button onClick={onBackToGrid} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>

            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                {MARKETPLACE_LOGOS[selectedMarketplace.shortName] ? (
                  <img src={MARKETPLACE_LOGOS[selectedMarketplace.shortName]} alt={selectedMarketplace.name} className="w-8 h-8 object-contain rounded" />
                ) : (
                  <span className="text-2xl">{selectedMarketplace.icon}</span>
                )}
                <h2 className="font-semibold text-slate-800">{selectedMarketplace.name}</h2>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">SKU bazlı fiyat hesaplama</p>
            </div>

            {/* Country Selector */}
            <div className="flex flex-col gap-1 ml-6 pl-6 border-l border-slate-200">
              {(() => {
                const activeCountries = selectedMarketplace.countries.filter(c => c.isActive);
                const midPoint = Math.ceil(activeCountries.length / 2);
                return (
                  <>
                    <div className="flex items-center gap-1">
                      {activeCountries.slice(0, midPoint).map(c => (
                        <button key={c.code} onClick={() => setSelectedCountry(c.code)}
                          className={`flex items-center gap-1 px-2 py-0.5 text-xs rounded-lg transition-colors ${
                            selectedCountry === c.code ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-slate-600 hover:bg-slate-100'
                          }`}>
                          <span>{c.flag}</span><span>{c.code}</span>
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-1">
                      {activeCountries.slice(midPoint).map(c => (
                        <button key={c.code} onClick={() => setSelectedCountry(c.code)}
                          className={`flex items-center gap-1 px-2 py-0.5 text-xs rounded-lg transition-colors ${
                            selectedCountry === c.code ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-slate-600 hover:bg-slate-100'
                          }`}>
                          <span>{c.flag}</span><span>{c.code}</span>
                        </button>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Fulfillment Tabs */}
            {fulfillmentOptions.length > 0 && (
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => setSelectedFulfillment('')}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                    !selectedFulfillment ? 'bg-indigo-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  title="Tüm kargo türleri"
                >
                  <span>Tümü</span>
                </button>
                {fulfillmentOptions.map(opt => (
                  <button key={opt.code} onClick={() => setSelectedFulfillment(opt.code)}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                      selectedFulfillment === opt.code
                        ? opt.code.includes('FBA') ? 'bg-orange-500 text-white shadow-sm' : 'bg-blue-600 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    title={opt.description}
                  >
                    {opt.code.includes('FBA') ? <Package className="w-4 h-4" /> : <Truck className="w-4 h-4" />}
                    <span>{opt.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-2 py-1 bg-slate-100 rounded-lg text-xs text-slate-600">
              <DollarSign className="w-3.5 h-3.5" />
              <span>1 USD = {exchangeRates[selectedCountryData?.currency || 'USD']?.toFixed(2) || '1.00'} {selectedCountryData?.currency || 'USD'}</span>
              <button onClick={onRefreshRates} disabled={isLoadingRates} className="p-0.5 hover:bg-slate-200 rounded" title="Kur güncelle">
                <RefreshCw className={`w-3 h-3 ${isLoadingRates ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <button onClick={() => { setViewMode('settings'); setSettingsTab('general'); }} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg" title="Ayarlar">
              <Settings className="w-4 h-4" />
            </button>
            <button onClick={() => setIsSKUImportOpen(true)} className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg">
              <Upload className="w-3.5 h-3.5" />
              SKU Import
            </button>
            <button onClick={exportData} className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg">
              <Download className="w-3.5 h-3.5" />
              Dışa Aktar
            </button>
          </div>
        </div>
      </div>

      {/* SKU Import Modal */}
      <MarketplaceSKUImportModal
        isOpen={isSKUImportOpen}
        onClose={() => setIsSKUImportOpen(false)}
        onImportComplete={async () => { const allProducts = await getAllProducts(); setProducts(allProducts); }}
        marketplaceName={selectedMarketplace.name}
        marketplaceShortName={selectedMarketplace.shortName}
        marketplaceId={selectedMarketplace.id}
        fulfillmentOptions={fulfillmentOptions}
        selectedFulfillment={selectedFulfillment}
        isAmazon={selectedMarketplace.shortName === 'AMZ'}
      />

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="SKU veya ürün ara..." className="w-full pl-9 pr-4 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white">
              <option value="">Tüm Kategoriler</option>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          <select value={filterParent} onChange={(e) => setFilterParent(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white">
            <option value="">Tüm Ürünler</option>
            {parentProducts.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {(searchTerm || filterCategory || filterParent) && (
            <button onClick={() => { setSearchTerm(''); setFilterCategory(''); setFilterParent(''); }}
              className="flex items-center gap-1 px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg">
              <X className="w-3 h-3" /> Temizle
            </button>
          )}
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input type="checkbox" checked={hideIncomplete} onChange={(e) => setHideIncomplete(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
            <span className="text-xs text-slate-600">Eksik bilgili gizle</span>
          </label>
          <div className="flex items-center gap-3 ml-auto">
            <span className="text-xs text-slate-500">{filteredProductRows.length} ürün gösteriliyor</span>
            {(searchTerm || filterCategory || filterParent || hideIncomplete) && (
              <span className="text-xs text-slate-400">/ {productRows.length} toplam</span>
            )}
          </div>
        </div>
      </div>

      {/* Pricing Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto overflow-y-auto max-h-[600px] border border-slate-200 rounded-lg">
          <table className="w-full divide-y divide-slate-200">
            <thead className="bg-slate-50 sticky top-0 z-20">
              <tr>
                <th className="px-2 py-2 text-left text-[11px] font-semibold text-slate-600 sticky left-0 bg-slate-50 z-30 border-r border-slate-200" style={{ minWidth: '200px' }}>
                  Ürün
                </th>
                <th className="px-2 py-2 text-left text-[11px] font-semibold text-slate-600" style={{ minWidth: '120px' }}>Kategori</th>
                <th className="px-2 py-2 text-left text-[11px] font-semibold text-slate-600 whitespace-nowrap" style={{ minWidth: '100px' }}>Marketplace SKU</th>
                {th('Maliyet')}
                {th('Desi')}
                <th className="px-2 py-2 text-right text-[11px] font-semibold text-green-700 whitespace-nowrap bg-green-50/50" style={{ minWidth: '90px' }}>Tavsiye Fiyat</th>
                <th className="px-2 py-2 text-right text-[11px] font-semibold text-emerald-700 whitespace-nowrap bg-emerald-50/50" style={{ minWidth: '70px' }}>Net Kar</th>
                <th className="px-2 py-2 text-right text-[11px] font-semibold text-emerald-700 whitespace-nowrap bg-emerald-50/50" style={{ minWidth: '55px' }}>Marj %</th>
                {th('Sell Fee %')}
                {showFbaFee && th('FBA Fee %')}
                {th('Refund %')}
                {th('VAT %')}
                {th('Ads %')}
                {showFbaCost && th('FBA Cost %')}
                {showFbmCost && th('FBM Cost %')}
                {th('Kargo')}
                {showCustoms && th('Gümrük %')}
                {showDdp && th('DDP')}
                {showWarehouse && th('Depo %')}
                {showGst && th('GST %')}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {filteredProductRows.map(row => {
                const { product } = row;
                const ft = selectedFulfillment || 'FBA';
                const b = calculateBreakdown(product, ft);
                const rate = exchangeRates[selectedCountryData?.currency || 'USD'] || 1;
                const baseCostLocal = (product.baseCost || 0) * rate;

                return (
                  <tr key={product.id} className="hover:bg-slate-50">
                    <td className="px-2 py-2 sticky left-0 bg-white z-10 border-r border-slate-100" style={{ minWidth: '200px', maxWidth: '280px' }}>
                      <div className="text-[11px] text-slate-800 leading-tight break-words">{product.name}</div>
                      {product.productSku && <div className="text-[9px] text-slate-400 font-mono truncate" title={product.productSku}>{product.productSku}</div>}
                    </td>
                    <td className="px-2 py-2" style={{ minWidth: '120px', maxWidth: '150px' }}>
                      <div className="text-[11px] text-slate-600 truncate">{product.category || '-'}</div>
                    </td>
                    <td className="px-2 py-2 overflow-hidden" style={{ minWidth: '100px', maxWidth: '140px' }}>
                      {renderSkuCell(row.allSkus, selectedFulfillment || undefined)}
                    </td>
                    <td className="px-2 py-2 text-right" style={{ minWidth: '55px' }}>
                      <div className="text-[11px] text-slate-700">{currencySymbol}{baseCostLocal ? baseCostLocal.toFixed(2) : '0.00'}</div>
                      <div className="text-[9px] text-slate-400">${(Number(product.baseCost) || 0).toFixed(2)}</div>
                    </td>
                    <td className="px-2 py-2 text-right" style={{ minWidth: '55px' }}>
                      <div className="text-[11px] text-slate-600">
                        {productDesi(product) ? productDesi(product).toFixed(1) : '-'}
                        {desiFromManual(product) ? <span className="ml-0.5 text-[8px] text-amber-500">M</span> : null}
                      </div>
                    </td>
                    <td className="px-2 py-2 bg-green-50/30 text-right" style={{ minWidth: '90px' }}>
                      <div className="text-[11px] font-semibold text-green-700">{currencySymbol}{b.advisedPrice.toFixed(2)}</div>
                      <div className="text-[9px] text-slate-400">Min: {currencySymbol}{b.sellingPrice.toFixed(2)}</div>
                    </td>
                    <td className="px-2 py-2 bg-emerald-50/50 text-right" style={{ minWidth: '70px' }}>
                      <div className={`text-[11px] font-semibold ${b.netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {currencySymbol}{b.netProfit.toFixed(2)}
                      </div>
                      <div className="text-[9px] text-red-400">-{currencySymbol}{b.totalExpenses.toFixed(2)}</div>
                    </td>
                    <td className="px-2 py-2 bg-emerald-50/50 text-right" style={{ minWidth: '55px' }}>
                      <div className={`text-[11px] font-semibold ${
                        b.marginPercent >= 20 ? 'text-emerald-600' :
                        b.marginPercent >= 10 ? 'text-yellow-600' :
                        b.marginPercent >= 0 ? 'text-orange-600' : 'text-red-600'
                      }`}>
                        {b.marginPercent.toFixed(1)}%
                      </div>
                    </td>
                    {pctCell(b.sellingFeePercent, b.sellingFeeAmount, currencySymbol)}
                    {showFbaFee && pctCell(b.fbaFeePercent, b.fbaFeeAmount, currencySymbol)}
                    {pctCell(b.refundLossPercent, b.refundLossAmount, currencySymbol)}
                    {pctCell(b.vatPercent, b.vatAmount, currencySymbol)}
                    {pctCell(b.adsPercent, b.adsAmount, currencySymbol)}
                    {showFbaCost && pctCell(b.fbaCostPercent, b.fbaCostAmount, currencySymbol)}
                    {showFbmCost && pctCell(b.fbmCostPercent, b.fbmCostAmount, currencySymbol)}
                    {fixedCell(b.shippingCost, currencySymbol, productDesi(product) ? `${productDesi(product).toFixed(1)} desi` : undefined)}
                    {showCustoms && pctCell(b.customsPercent, b.customsAmount, currencySymbol)}
                    {showDdp && fixedCell(b.ddpFee, currencySymbol)}
                    {showWarehouse && pctCell(b.warehousePercent, b.warehouseAmount, currencySymbol)}
                    {showGst && pctCell(b.gstPercent, b.gstAmount, currencySymbol)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredProductRows.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            {searchTerm || filterCategory || filterParent ? 'Arama sonucu bulunamadı' : 'Henüz ürün yok. Dashboard > Import Products ile ekleyin.'}
          </div>
        )}
      </div>

      {/* Price Simulator */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <button
          onClick={() => setShowSimulator(!showSimulator)}
          className="flex items-center justify-between w-full px-4 py-3 hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-medium text-slate-700">Fiyat Simülatörü</span>
          </div>
          {showSimulator ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>

        {showSimulator && (() => {
          const ft = selectedFulfillment || 'FBA';
          const fakeProduct: Product = {
            id: '__simulator__', name: 'Simülasyon', productSku: '', category: simCategory || '__default__',
            baseCost: simCost, size: simDesi, createdAt: new Date(), updatedAt: new Date(), source: 'manual',
          };
          const simResult = (simCost > 0 && simDesi > 0) ? calculateBreakdown(fakeProduct, ft) : EMPTY_BREAKDOWN;
          const sym = currencySymbol;

          return (
            <div className="px-4 pb-4 border-t border-slate-100">
              <div className="grid grid-cols-4 gap-3 mt-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Maliyet (USD)</label>
                  <input type="number" value={simCost || ''} onChange={(e) => setSimCost(parseFloat(e.target.value) || 0)}
                    placeholder="0.00" step="0.01" min="0"
                    className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Desi</label>
                  <input type="number" value={simDesi || ''} onChange={(e) => setSimDesi(parseFloat(e.target.value) || 0)}
                    placeholder="0.0" step="0.1" min="0"
                    className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Kategori</label>
                  <select value={simCategory} onChange={(e) => setSimCategory(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white">
                    <option value="">Genel (Varsayılan)</option>
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Fulfillment</label>
                  <div className="px-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-700">
                    {ft}
                  </div>
                </div>
              </div>

              {simCost > 0 && simDesi > 0 && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <div className="grid grid-cols-5 gap-3">
                    <div className="text-center">
                      <div className="text-[10px] text-slate-500">Tavsiye Fiyat</div>
                      <div className="text-sm font-bold text-green-700">{sym}{simResult.advisedPrice.toFixed(2)}</div>
                      <div className="text-[9px] text-slate-400">Min: {sym}{simResult.sellingPrice.toFixed(2)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] text-slate-500">Net Kar</div>
                      <div className={`text-sm font-bold ${simResult.netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {sym}{simResult.netProfit.toFixed(2)}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] text-slate-500">Marj</div>
                      <div className={`text-sm font-bold ${simResult.marginPercent >= 20 ? 'text-emerald-600' : simResult.marginPercent >= 0 ? 'text-orange-600' : 'text-red-600'}`}>
                        {simResult.marginPercent.toFixed(1)}%
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] text-slate-500">Toplam Masraf</div>
                      <div className="text-sm font-semibold text-slate-700">{sym}{simResult.totalExpenses.toFixed(2)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] text-slate-500">Ürün Maliyeti</div>
                      <div className="text-sm font-semibold text-slate-700">{sym}{simResult.productCost.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-6 gap-2 mt-2 pt-2 border-t border-slate-200">
                    <div className="text-center">
                      <div className="text-[9px] text-slate-400">Sell Fee</div>
                      <div className="text-[11px] text-slate-600">{simResult.sellingFeePercent.toFixed(1)}%</div>
                    </div>
                    {isFBA && <div className="text-center">
                      <div className="text-[9px] text-slate-400">FBA Fee</div>
                      <div className="text-[11px] text-slate-600">{simResult.fbaFeePercent.toFixed(1)}%</div>
                    </div>}
                    <div className="text-center">
                      <div className="text-[9px] text-slate-400">Refund</div>
                      <div className="text-[11px] text-slate-600">{simResult.refundLossPercent.toFixed(1)}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[9px] text-slate-400">VAT</div>
                      <div className="text-[11px] text-slate-600">{simResult.vatPercent.toFixed(1)}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[9px] text-slate-400">Ads</div>
                      <div className="text-[11px] text-slate-600">{simResult.adsPercent.toFixed(1)}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[9px] text-slate-400">Kargo</div>
                      <div className="text-[11px] text-slate-600">{sym}{simResult.shippingCost.toFixed(2)}</div>
                    </div>
                    {isFBA && <div className="text-center">
                      <div className="text-[9px] text-slate-400">FBA Cost</div>
                      <div className="text-[11px] text-slate-600">{simResult.fbaCostPercent.toFixed(1)}%</div>
                    </div>}
                    {isFBM && <div className="text-center">
                      <div className="text-[9px] text-slate-400">FBM Cost</div>
                      <div className="text-[11px] text-slate-600">{simResult.fbmCostPercent.toFixed(1)}%</div>
                    </div>}
                    {showCustoms && <div className="text-center">
                      <div className="text-[9px] text-slate-400">Gümrük</div>
                      <div className="text-[11px] text-slate-600">{simResult.customsPercent.toFixed(1)}%</div>
                    </div>}
                    {showDdp && <div className="text-center">
                      <div className="text-[9px] text-slate-400">DDP</div>
                      <div className="text-[11px] text-slate-600">{sym}{simResult.ddpFee.toFixed(2)}</div>
                    </div>}
                    {showWarehouse && <div className="text-center">
                      <div className="text-[9px] text-slate-400">Depo</div>
                      <div className="text-[11px] text-slate-600">{simResult.warehousePercent.toFixed(1)}%</div>
                    </div>}
                    {showGst && <div className="text-center">
                      <div className="text-[9px] text-slate-400">GST</div>
                      <div className="text-[11px] text-slate-600">{simResult.gstPercent.toFixed(1)}%</div>
                    </div>}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default MarketplacePricingTable;
